import React from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Headphones, LogOut } from 'lucide-react';
import { useModalGuard } from '../../contexts/ModalGuardContext';
import { usePrivacyMode } from '../../contexts/PrivacyModeContext';
import HideAmountsToggle from './HideAmountsToggle';
import PrivacyModeShield from './PrivacyModeShield';

const DashboardHeader = ({ user, handleRestartTour, handleLogout, setShowQuickSetupModal, onUpgradeClick }) => {
  const { guard } = useModalGuard();
  const { privacyMode } = usePrivacyMode();
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="container mx-auto px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center space-x-1 sm:space-x-2 cursor-pointer" onClick={() => window.location.href = '/'}>
              <img 
                src="https://customer-assets.emergentagent.com/job_3876ffd2-0b67-4d0f-be27-32eb0667fc3a/artifacts/g551041i_MIF%20Logo%20White.png" 
                alt="MyInvestFolio Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
              />
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-serif font-bold text-slate-900">
                My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span>
              </h1>
            </div>
            {/* Tier badge intentionally removed from header per Privacy Uplift Ch. 2.
                Members can view + manage their tier inside Settings. Keeping the top
                bar quiet stops the tier itself from being a wealth signal to anyone
                glancing at the screen. Upgrade CTA remains — subtle text link only. */}
            {user && onUpgradeClick && user.tier === 'portfolio_continuity' && (
              <button
                type="button"
                onClick={onUpgradeClick}
                className="hidden sm:inline text-[11px] text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline font-medium"
                data-testid="header-upgrade-continuity-link"
              >
                Upgrade to Portfolio Clarity &rarr;
              </button>
            )}
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.location.href = '/'} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium px-2 sm:px-3">
              <span className="hidden sm:inline">Home</span>
              <span className="sm:hidden">&#127968;</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => guard(handleRestartTour, 'dialog')} className="text-slate-600 hover:text-purple-600 hover:bg-purple-50 transition-colors font-medium px-2 sm:px-3" title="Replay the onboarding tour">
              <Sparkles className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Tour</span>
            </Button>
            {/* Hide Amounts eye toggle — Privacy Uplift Q1 */}
            <HideAmountsToggle />
            {/* Privacy Mode shield — Ch. 6 master switch */}
            <PrivacyModeShield />
            {user && (
              <div className="flex items-center space-x-1 sm:space-x-3">
                {user.picture && !privacyMode && (
                  <img src={user.picture} alt={user.code_name || user.name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" />
                )}
                <span
                  className="hidden md:inline text-slate-700 font-medium truncate max-w-[160px] tracking-wide"
                  data-testid="header-code-name"
                  title={privacyMode ? 'Privacy Mode is on. Your Code Name is hidden.' : 'Your private Code Name. Change it in Your Privacy.'}
                >
                  {privacyMode ? 'MEMBER' : (user.code_name || user.name)}
                </span>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => guard(() => setShowQuickSetupModal(true), 'dialog')} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors font-medium px-2 sm:px-3" title="Book a quick 10-15 min guided call" data-testid="quick-setup-call-btn">
              <Headphones className="w-4 h-4 sm:mr-2" />
              <span className="hidden lg:inline">Quick Virtual Setup</span>
            </Button>
            <Button variant="ghost" size="sm" data-testid="logout-btn" onClick={handleLogout} className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 transition-colors font-medium px-2 sm:px-3">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
